import React from 'react';
import { 
  Box,
  Typography,
  IconButton,
  Drawer,
} from '@mui/material';
import { 
  CopyAll as CopyAllIcon,
  OpenInNew as OpenInNewIcon,
  Language as LanguageIcon,
  TextFields as TextFieldsIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import html2pdf from 'html2pdf.js';
import { useDispatch, useSelector } from 'react-redux';
import { resetSelectedKnowledgeBase } from '../../../redux/actions/knowledgeBaseActions';
import { showSuccess } from '../../../Components/reuse/Toast';
import LinearLoading from '../../../Components/reuse/LinearLoading';

function DocumentDetailSidebar({ open, onClose }) {
  const dispatch = useDispatch();
  const { selectedKnowledgeBase, selectedLoading } = useSelector((state) => state.knowledgeBase);

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'url':
        return <LanguageIcon sx={{ color: '#6b7280', mr: 1, fontSize: 20 }} />;
      case 'text':
        return <TextFieldsIcon sx={{ color: '#6b7280', mr: 1, fontSize: 20 }} />;
      default:
        return <LanguageIcon sx={{ color: '#6b7280', mr: 1, fontSize: 20 }} />;
    }
  };

  const getDocumentContent = (item) => {
    switch (item.type) {
      case 'url':
        return item.knowledgeBaseUrl || item.url || 'No URL provided';
      case 'text':
        return item.knowledgeBaseText || 'No text content';
      default:
        return item.knowledgeBaseUrl || item.url || 'No content provided';
    }
  };

  const handleDownloadPdf = () => {
    if (!selectedKnowledgeBase) return;
    const title = (selectedKnowledgeBase.name || 'Document').replace(/[^a-z0-9\-_ ]/gi, '');
    const htmlContent = selectedKnowledgeBase.extracted_inner_html || '<p>No content available</p>';

    const container = document.createElement('div');
    container.style.width = '800px';
    container.style.maxWidth = '800px';
    container.style.padding = '0';
    container.innerHTML = `
      <style>
        * { box-sizing: border-box; }
        h1 { font-size: 18px; margin: 0 0 12px 0; color:#111827; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; page-break-after: avoid; }
        h2, h3 { font-size: 15px; margin: 14px 0 8px 0; color:#111827; page-break-after: avoid; }
        p, li, div { font-size: 12px; line-height: 1.6; color:#111827; word-break: break-word; overflow-wrap: anywhere; }
        a { color: #1d4ed8; text-decoration: underline; }
        img, svg { max-width: 100%; height: auto; }
        table { width: 100%; border-collapse: collapse; }
        td, th { word-break: break-word; overflow-wrap: anywhere; }
      </style>
      <h1>${title}</h1>
      <div>${htmlContent}</div>
    `;

    const opt = {
      // Apply margins on every page (top,right,bottom,left)
      margin:       [36, 28, 36, 28],
      filename:     `${title || 'document'}.pdf`,
      image:        { type: 'jpeg', quality: 0.95 },
      html2canvas:  { scale: 2, useCORS: true, allowTaint: true, scrollY: 0 },
      jsPDF:        { unit: 'pt', format: 'a4', orientation: 'portrait' },
      pagebreak:    { mode: ['css','legacy'] }
    };

    html2pdf().from(container).set(opt).save().finally(() => {
      container.remove();
    });
  };

  const handleClose = () => {
    onClose();
    dispatch(resetSelectedKnowledgeBase());
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100vw', sm: '600px', md: '800px' },
          maxWidth: '90vw',
          boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.1)',
          bgcolor: '#f8fafc'
        }
      }}
      PaperProps={{
        sx: {
          marginTop: '64px', // Adjust based on your header height
          height: 'calc(100% - 64px)'
        }
      }}
    >
      {selectedLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LinearLoading show={true} />
        </Box>
      ) : selectedKnowledgeBase ? (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc' }}>
          {/* Header with URL and close button */}
          <Box sx={{ 
            p: 3, 
            bgcolor: '#f8fafc', 
            position: 'relative',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, pr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getDocumentIcon(selectedKnowledgeBase.type)}
                  <Typography variant="h6" sx={{ 
                    fontWeight: 600, 
                    color: '#111827', 
                    fontSize: '18px'
                  }}>
                    {selectedKnowledgeBase.name || 'Document Details'}
                  </Typography>
                </Box>
                {selectedKnowledgeBase.type === 'url' && getDocumentContent(selectedKnowledgeBase) && (
                  <Box 
                    component="a"
                    href={getDocumentContent(selectedKnowledgeBase)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      color: '#6b7280', 
                      wordBreak: 'break-all', 
                      fontSize: '13px',
                      lineHeight: 1.4,
                      textDecoration: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        color: '#374151',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    <Typography sx={{ fontSize: '13px', lineHeight: 1, display: 'inline' }}>
                      {getDocumentContent(selectedKnowledgeBase)}
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: '10px', color: '#6b7280', ml: 0.5 }} />
                  </Box>
                )}
              </Box>
              <IconButton 
                onClick={handleClose} 
                size="small"
                sx={{ 
                  color: '#6b7280',
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  '&:hover': { 
                    bgcolor: '#f3f4f6',
                    borderColor: '#d1d5db'
                  },
                  width: 32,
                  height: 32
                }}
              >
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 400 }}>×</Typography>
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 3 }}>
            {/* Document ID Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#111827', fontSize: '16px' }}>
                Document ID
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    bgcolor: '#ffffff', 
                    px: 2, 
                    py: 1, 
                    borderRadius: 1, 
                    border: '1px solid #e5e7eb',
                    fontSize: '14px',
                    color: '#374151'
                  }}
                >
                  {selectedKnowledgeBase.id}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    navigator.clipboard.writeText(selectedKnowledgeBase.id);
                    showSuccess('Document ID copied to clipboard!');
                  }}
                  sx={{ 
                    bgcolor: '#f3f4f6',
                    '&:hover': { bgcolor: '#e5e7eb' }
                  }}
                >
                  <CopyAllIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Content Section */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', fontSize: '16px' }}>
                  {selectedKnowledgeBase.type === 'url' ? 'URL Content' : 'Text Content'}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={handleDownloadPdf}
                  title="Download as PDF"
                  sx={{
                    bgcolor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: '#f3f4f6', borderColor: '#d1d5db' }
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                </IconButton>
              </Box>
              {/* {selectedKnowledgeBase.type === 'url' ?  */}
              
                <Box 
                  sx={{ 
                    bgcolor: '#ffffff',
                    minHeight: '400px',
                    overflow: 'auto',
                    px: 2, 
                    '& h3': {
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#111827',
                      mb: 2,
                      mt: 3
                    },
                    '& p': {
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#374151',
                      mb: 2
                    },
                    '& div[data-name]': {
                      mb: 3
                    }
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: selectedKnowledgeBase.extracted_inner_html || '<p>No content available</p>' 
                  }}
                />
              
              {/* : (
                <Box 
                  sx={{ 
                    bgcolor: '#ffffff',
                    minHeight: '400px',
                    overflow: 'auto',
                    px: 2,
                    py: 2,
                    border: '1px solid #e5e7eb',
                    borderRadius: 1
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '14px',
                      lineHeight: 1.6,
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {getDocumentContent(selectedKnowledgeBase) || 'No text content available'}
                  </Typography>
                </Box>
              )} */}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
          <Typography variant="body1">
            No document details available
          </Typography>
        </Box>
      )}
    </Drawer>
  );
}

export default DocumentDetailSidebar;
